import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient } from "@/lib/api";
import { formatGNF } from "@/utils/format";
import { KpiCard, RangeSelector } from "@/components/dashboard/DashboardWidgets";
import ExportButtons from "@/components/dashboard/ExportButtons";
import RestaurantDashboardWidget from "@/components/dashboard/RestaurantDashboardWidget";
import DriverDashboardWidget from "@/components/dashboard/DriverDashboardWidget";
import AdminIncidentsPanel from "@/components/dashboard/AdminIncidentsPanel";
import UsersAdminPanel from "@/components/admin/UsersAdminPanel";
import { TrendingUp, Percent, ShoppingBag, Store, Bike, Users2 } from "lucide-react";
import { downloadCsv, downloadPdf, todayStamp } from "@/utils/exportHelpers";

export default function AdminDashboard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("summary"); // summary | restaurant | driver
  const [selectedRestaurant, setSelectedRestaurant] = useState("");
  const [selectedDriver, setSelectedDriver] = useState("");

  useEffect(() => {
    apiClient.get(`/dashboard/admin/summary?days=${days}`).then(({ data }) => setData(data)).catch(() => {});
  }, [days]);

  const restaurantOptions = useMemo(() => data?.restaurants || [], [data]);
  const driverOptions = useMemo(() => data?.drivers || [], [data]);

  useEffect(() => {
    if (!selectedRestaurant && restaurantOptions.length) setSelectedRestaurant(restaurantOptions[0].restaurant_id);
  }, [restaurantOptions, selectedRestaurant]);
  useEffect(() => {
    if (!selectedDriver && driverOptions.length) setSelectedDriver(driverOptions[0].driver_id);
  }, [driverOptions, selectedDriver]);

  const filenameBase = `neldaari-admin-${days}j-${todayStamp()}`;

  const buildAdminCsv = useCallback(() => {
    if (!data) return;
    const k = data.kpis || {};
    downloadCsv(`${filenameBase}.csv`, [
      {
        title: t("dashboard.adminTitle"),
        headers: [t("dashboard.pdfPeriod"), `${days}j`],
        rows: [],
      },
      {
        title: t("dashboard.pdfSection.kpis"),
        headers: [t("dashboard.adminRevenue"), t("dashboard.adminCommission"), t("dashboard.adminOrders"), t("dashboard.activeRestos")],
        rows: [[k.revenue ?? 0, k.commission ?? 0, k.orders ?? 0, `${k.active_restaurants ?? 0} / ${k.total_restaurants ?? 0}`]],
      },
      {
        title: t("dashboard.pdfSection.restaurants"),
        headers: [t("dashboard.colRestaurant"), t("dashboard.colRating"), t("dashboard.colOrders"), t("dashboard.colRevenue"), t("dashboard.colCommission")],
        rows: (data.restaurants || []).map((r) => [r.name, r.rating ?? "", r.orders ?? 0, r.revenue ?? 0, r.commission ?? 0]),
      },
      {
        title: t("dashboard.pdfSection.drivers"),
        headers: [t("dashboard.colDriver"), t("dashboard.colPhone"), t("dashboard.colDeliveries"), t("dashboard.colEarnings")],
        rows: (data.drivers || []).map((d) => [d.name, d.phone || "", d.deliveries ?? 0, d.earnings ?? 0]),
      },
    ]);
  }, [data, days, filenameBase, t]);

  const buildAdminPdf = useCallback(() => {
    if (!data) return;
    const k = data.kpis || {};
    const now = new Date();
    downloadPdf(`${filenameBase}.pdf`, {
      title: t("dashboard.adminTitle"),
      subtitle: t("dashboard.adminSubtitle"),
      period: `${t("dashboard.pdfPeriod")}: ${days}${lang === "en" ? "d" : "j"}`,
      generatedLabel: `${t("dashboard.pdfGeneratedOn")} ${now.toLocaleString(lang === "en" ? "en-US" : "fr-FR")}`,
      sections: [
        {
          title: t("dashboard.pdfSection.kpis"),
          kvs: [
            [t("dashboard.adminRevenue"), formatGNF(k.revenue)],
            [t("dashboard.adminCommission"), formatGNF(k.commission)],
            [t("dashboard.adminOrders"), k.orders ?? 0],
            [t("dashboard.activeRestos"), `${k.active_restaurants ?? 0} / ${k.total_restaurants ?? 0}`],
          ],
        },
        {
          title: t("dashboard.pdfSection.restaurants"),
          headers: [t("dashboard.colRestaurant"), t("dashboard.colRating"), t("dashboard.colOrders"), t("dashboard.colRevenue"), t("dashboard.colCommission")],
          rows: (data.restaurants || []).map((r) => [r.name, r.rating ?? "—", r.orders ?? 0, formatGNF(r.revenue), formatGNF(r.commission)]),
        },
        {
          title: t("dashboard.pdfSection.drivers"),
          headers: [t("dashboard.colDriver"), t("dashboard.colPhone"), t("dashboard.colDeliveries"), t("dashboard.colEarnings")],
          rows: (data.drivers || []).map((d) => [d.name, d.phone || "—", d.deliveries ?? 0, formatGNF(d.earnings)]),
        },
      ],
    });
  }, [data, days, filenameBase, lang, t]);

  if (!user || user.role !== "admin") {
    return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-mute">{t("admin.accessDenied")}</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-xs uppercase tracking-[0.25em] text-brand-red">{t("dashboard.adminSubtitle")}</div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-semibold">{t("dashboard.adminTitle")}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {tab === "summary" && data && (
            <ExportButtons onExportPdf={buildAdminPdf} onExportCsv={buildAdminCsv} testidPrefix="admin" />
          )}
          <RangeSelector value={days} onChange={setDays} testidPrefix="admin-range" />
        </div>
      </div>

      {/* TABS */}
      <div className="flex gap-2 mb-6 overflow-x-auto no-scrollbar" data-testid="admin-tabs">
        {[
          { k: "summary", label: t("dashboard.platformView") },
          { k: "restaurant", label: t("dashboard.byRestaurant") },
          { k: "driver", label: t("dashboard.byDriver") },
          { k: "incidents", label: t("admin.tabIncidents") },
          { k: "users", label: t("admin.tabUsers") },
        ].map((tt) => (
          <button
            key={tt.k}
            onClick={() => setTab(tt.k)}
            data-testid={`admin-tab-${tt.k}`}
            className={`rounded-full px-4 py-2 text-sm font-semibold border ${tab === tt.k ? "bg-ink text-white border-ink" : "bg-white border-sand hover:border-brand-red"}`}
          >
            {tt.label}
          </button>
        ))}
      </div>

      {tab === "summary" && data && (
        <div className="space-y-6" data-testid="admin-summary">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiCard icon={<TrendingUp className="w-4 h-4" />} label={t("dashboard.adminRevenue")} value={formatGNF(data.kpis.revenue)} growthPct={data.kpis.growth.revenue_pct} color="#E32636" testid="admin-kpi-revenue" />
            <KpiCard icon={<Percent className="w-4 h-4" />} label={t("dashboard.adminCommission")} value={formatGNF(data.kpis.commission)} growthPct={data.kpis.growth.commission_pct} color="#009E60" testid="admin-kpi-commission" />
            <KpiCard icon={<ShoppingBag className="w-4 h-4" />} label={t("dashboard.adminOrders")} value={data.kpis.orders} growthPct={data.kpis.growth.orders_pct} color="#F4C430" testid="admin-kpi-orders" />
            <KpiCard icon={<Store className="w-4 h-4" />} label={t("dashboard.activeRestos")} value={`${data.kpis.active_restaurants} / ${data.kpis.total_restaurants}`} color="#1A1A1A" testid="admin-kpi-restos" />
          </div>

          {/* Restaurants leaderboard */}
          <div className="bg-white border border-sand rounded-2xl overflow-hidden">
            <h3 className="font-semibold p-4 pb-2 flex items-center gap-2"><Store className="w-4 h-4 text-brand-red" /> {t("dashboard.restaurantsLeader", { n: data.restaurants.length, d: days })}</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-widest text-mute bg-elevated">
                  <tr>
                    <th className="text-left px-4 py-2">{t("dashboard.colRestaurant")}</th>
                    <th className="text-right px-4 py-2">{t("dashboard.colRating")}</th>
                    <th className="text-right px-4 py-2">{t("dashboard.colOrders")}</th>
                    <th className="text-right px-4 py-2">{t("dashboard.colRevenue")}</th>
                    <th className="text-right px-4 py-2">{t("dashboard.colCommission")}</th>
                    <th className="text-right px-4 py-2">{t("dashboard.colDetails")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.restaurants.map((r) => (
                    <tr key={r.restaurant_id} className="border-t border-sand" data-testid={`row-rest-${r.restaurant_id}`}>
                      <td className="px-4 py-2.5">
                        <span className="font-medium">{r.name}</span>
                        {r.status !== "active" && <span className="ml-2 text-[10px] uppercase text-brand-red">{r.status}</span>}
                      </td>
                      <td className="px-4 py-2.5 text-right">⭐ {r.rating ?? "—"}</td>
                      <td className="px-4 py-2.5 text-right">{r.orders}</td>
                      <td className="px-4 py-2.5 text-right font-semibold">{formatGNF(r.revenue)}</td>
                      <td className="px-4 py-2.5 text-right text-brand-green">{formatGNF(r.commission)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => { setSelectedRestaurant(r.restaurant_id); setTab("restaurant"); }}
                          data-testid={`view-rest-${r.restaurant_id}`}
                          className="text-xs text-brand-red hover:underline"
                        >{t("dashboard.seeMore")}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Drivers leaderboard */}
          <div className="bg-white border border-sand rounded-2xl overflow-hidden">
            <h3 className="font-semibold p-4 pb-2 flex items-center gap-2"><Bike className="w-4 h-4 text-brand-red" /> {t("dashboard.driversLeader", { a: data.kpis.active_drivers, t: data.kpis.total_drivers, d: days })}</h3>
            {data.drivers.length === 0 ? (
              <div className="text-mute text-sm p-4">{t("dashboard.noDeliveriesPeriod")}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-widest text-mute bg-elevated">
                    <tr>
                      <th className="text-left px-4 py-2">{t("dashboard.colDriver")}</th>
                      <th className="text-left px-4 py-2">{t("dashboard.colPhone")}</th>
                      <th className="text-right px-4 py-2">{t("dashboard.colDeliveries")}</th>
                      <th className="text-right px-4 py-2">{t("dashboard.colEarnings")}</th>
                      <th className="text-right px-4 py-2">{t("dashboard.colDetails")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.drivers.map((d) => (
                      <tr key={d.driver_id} className="border-t border-sand" data-testid={`row-drv-${d.driver_id}`}>
                        <td className="px-4 py-2.5"><span className="font-medium">{d.name}</span></td>
                        <td className="px-4 py-2.5 text-mute text-xs">{d.phone || "—"}</td>
                        <td className="px-4 py-2.5 text-right">{d.deliveries}</td>
                        <td className="px-4 py-2.5 text-right font-semibold text-brand-green">{formatGNF(d.earnings)}</td>
                        <td className="px-4 py-2.5 text-right">
                          <button
                            onClick={() => { setSelectedDriver(d.driver_id); setTab("driver"); }}
                            data-testid={`view-drv-${d.driver_id}`}
                            className="text-xs text-brand-red hover:underline"
                          >{t("dashboard.seeMore")}</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "restaurant" && (
        <div className="space-y-4" data-testid="admin-per-restaurant">
          <div className="bg-white border border-sand rounded-2xl p-3 flex items-center gap-2">
            <Store className="w-4 h-4 text-brand-red" />
            <label className="text-xs uppercase tracking-widest text-mute mr-2">{t("dashboard.selectRestaurant")}</label>
            <select
              value={selectedRestaurant}
              onChange={(e) => setSelectedRestaurant(e.target.value)}
              data-testid="admin-select-restaurant"
              className="bg-elevated rounded-xl px-3 py-2 text-sm outline-none flex-1"
            >
              {restaurantOptions.map((r) => (
                <option key={r.restaurant_id} value={r.restaurant_id}>{r.name} — {formatGNF(r.revenue)}</option>
              ))}
            </select>
          </div>
          {selectedRestaurant && <RestaurantDashboardWidget restaurantId={selectedRestaurant} />}
        </div>
      )}

      {tab === "driver" && (
        <div className="space-y-4" data-testid="admin-per-driver">
          <div className="bg-white border border-sand rounded-2xl p-3 flex items-center gap-2">
            <Users2 className="w-4 h-4 text-brand-red" />
            <label className="text-xs uppercase tracking-widest text-mute mr-2">{t("dashboard.selectDriver")}</label>
            <select
              value={selectedDriver}
              onChange={(e) => setSelectedDriver(e.target.value)}
              data-testid="admin-select-driver"
              className="bg-elevated rounded-xl px-3 py-2 text-sm outline-none flex-1"
            >
              {driverOptions.length === 0 && <option value="">{t("dashboard.noActiveDriver")}</option>}
              {driverOptions.map((d) => (
                <option key={d.driver_id} value={d.driver_id}>{d.name} — {d.deliveries}</option>
              ))}
            </select>
          </div>
          {selectedDriver && <DriverDashboardWidget driverId={selectedDriver} />}
        </div>
      )}

      {tab === "incidents" && <AdminIncidentsPanel />}
      {tab === "users" && <UsersAdminPanel />}
    </div>
  );
}
